// Stage 3.9 (Enterprise Candidate Experience Final UI Polish & UX Optimization): the centralized
// language resource. Phase C.5 (Candidate System UI Language Migration) moved modules/i18n.js's 2
// former consumers (RegistrationMessage.jsx, NavigationTab_new_one.jsx) onto this dictionary — see the
// registrationMessage/navigationTab namespaces below — so modules/i18n.js itself is no longer imported
// anywhere in the app (kept in place, unused, per that phase's own deferred-cleanup policy rather than
// deleted). This is a separate, larger dictionary consumed only by the new components/Text/Text.jsx primitive, organized
// by namespace (one object per page/area) so entries stay findable as coverage grows — every entry is
// { en, mr } (hi omitted unless/until a page actually needs it, unlike modules/i18n.js's fixed 3-locale
// shape, since EnabledLanguages defaults to "en,mr" only — see DBScript/Security2025/
// 09_SeedLanguageConfiguration.sql).
//
// Per-page migration namespaces live in modules/i18n/pages/*.js (one file per view, so parallel
// migration passes never conflict on this single aggregator file) and are merged in below. See each
// pages/*.js file's own header comment for that page's extraction-vs-AI-authored breakdown.
import registration from './pages/registration';
import personalInfo from './pages/personalInfo';
import qualificationInfo from './pages/qualificationInfo';
import reservationDetails from './pages/reservationDetails';
import imageSignature from './pages/imageSignature';
import preview from './pages/preview';
import challanDetails from './pages/challanDetails';
import submitForm from './pages/submitForm';
import homeSummary from './pages/homeSummary';
import forgotPassword from './pages/forgotPassword';
// Phase C.5 (Candidate System UI Language Migration)
import examCard from './pages/examCard';
import receiptDetails from './pages/receiptDetails';
import interviewCallLetter from './pages/interviewCallLetter';

const DICTIONARY = {
  accessibilityToolbar: {
    fontSize: { en: 'Font Size', mr: 'फॉन्ट आकार' },
    decreaseFontSize: { en: 'Decrease font size', mr: 'फॉन्ट आकार कमी करा' },
    resetFontSize: { en: 'Reset font size', mr: 'फॉन्ट आकार रीसेट करा' },
    increaseFontSize: { en: 'Increase font size', mr: 'फॉन्ट आकार वाढवा' },
    highContrast: { en: 'High Contrast', mr: 'उच्च कॉन्ट्रास्ट' },
    // Phase C.3: language/displayModeBilingual/displayModeEnglish/displayModeRegional are no longer
    // referenced (the candidate-facing language/display-mode selectors were removed) — left in place,
    // unused, rather than deleted, since removing dictionary entries carries no benefit and only risks
    // being wrong if some other consumer is later found to reference them.
    language: { en: 'Language', mr: 'भाषा' },
    displayModeBilingual: { en: 'Bilingual', mr: 'द्विभाषिक' },
    displayModeEnglish: { en: 'English', mr: 'इंग्रजी' },
    displayModeRegional: { en: 'Regional', mr: 'प्रादेशिक' },
    // Corrected (not new content) for accuracy now that no language control remains in this toolbar.
    accessibilityOptions: { en: 'Accessibility Options', mr: 'सुलभता पर्याय' }
  },

  // Shared by ResetPassword.jsx and SetPassword.js — both English-only today (Finding 7: no existing
  // Marathi anywhere in either file). Marathi text below is AI-authored, matching the terminology
  // modules/i18n.js already uses for password/login concepts (पासवर्ड/लॉग इन) — NOT reviewed by a
  // native/certified translator; see ERPM_S3_9_Accessibility_Verification.md for the required
  // linguistic-QA follow-up before production release.
  passwordCommon: {
    newPasswordLabel: { en: 'New Password', mr: 'नवीन पासवर्ड' },
    confirmPasswordLabel: { en: 'Confirm Password', mr: 'पासवर्डची पुष्टी करा' },
    pleaseEnterNewPassword: { en: 'Please Enter New Password', mr: 'कृपया नवीन पासवर्ड प्रविष्ट करा' },
    pleaseEnterConfirmPassword: { en: 'Please Enter Confirm Password', mr: 'कृपया पासवर्डची पुष्टी प्रविष्ट करा' },
    passwordsDoNotMatch: { en: 'New Password and Confirm Password are not same', mr: 'नवीन पासवर्ड आणि पासवर्डची पुष्टी समान नाहीत' },
    passwordComplexity: {
      en: 'Password must contain 8-20 characters & have at least 1 upper case,1 lower case letter, numbers and special characters.',
      mr: 'पासवर्डमध्ये ८-२० अक्षरे असावीत आणि किमान १ मोठे अक्षर, १ लहान अक्षर, अंक आणि विशेष चिन्ह असणे आवश्यक आहे.'
    },
    goToLogin: { en: 'Go To Login', mr: 'लॉगिन पृष्ठावर जा' }
  },
  resetPassword: {
    heading: { en: 'Reset Password', mr: 'पासवर्ड रीसेट करा' },
    submitButton: { en: 'Reset Password', mr: 'पासवर्ड रीसेट करा' }
  },
  setPassword: {
    heading: { en: 'Set Password', mr: 'पासवर्ड सेट करा' },
    submitButton: { en: 'Set Password', mr: 'पासवर्ड सेट करा' }
  },

  // NewLogin.jsx. "New Registration"/"Registered Candidate Login"/the 2 validation messages had no
  // existing Marathi anywhere in the file — AI-authored, needs linguistic QA (see Finding 7). The other
  // entries below (Application ID/Password/Forgot Password?/Login) are extractions of text that was
  // already bilingual in the file (zero new translation authored for those).
  login: {
    newRegistration: { en: 'New Registration', mr: 'नवीन नोंदणी' },
    registeredCandidateLogin: { en: 'Registered Candidate Login', mr: 'नोंदणीकृत उमेदवार लॉगिन' },
    applicationIdLabel: { en: 'Application ID', mr: 'अर्ज आयडी' },
    passwordLabel: { en: 'Password', mr: 'पासवर्ड' },
    enterPassword: { en: 'Enter Password', mr: 'पासवर्ड टाका' },
    forgotPassword: { en: 'Forgot Password?', mr: 'पासवर्ड विसरलात?' },
    loginButton: { en: 'Login', mr: 'लॉगिन करा' },
    invalidApplicationId: { en: 'Invalid Application ID', mr: 'अवैध अर्ज आयडी' },
    invalidPassword: { en: 'Invalid Password', mr: 'अवैध पासवर्ड' }
  },

  // LandingPage_new_one.jsx. All 4 entries below were already bilingual in the file — extraction only,
  // zero new translation authored.
  landingPage: {
    importantDocumentsHeading: { en: 'Important Documents and Notifications', mr: 'महत्त्वाची कागदपत्रे आणि सूचना' },
    userGuideLink: { en: 'User Guide for Computer Based Test', mr: 'कंप्यूटर आधारित चाचणीसाठी वापरकर्ता मार्गदर्शक' },
    importantInstructionsLink: { en: 'Important Instructions for Candidates', mr: 'उमेदवारांसाठी महत्त्वाच्या सूचना' },
    candidateAccessHeading: { en: 'Candidate Access', mr: 'उमेदवार प्रवेश' }
  },

  // LandingPage_new_one.jsx (Phase C.6.1 — Candidate Homepage & Login Modernization): wires the
  // homepage to the Publication foundation (Phase C.1) for the first time. publicationsHeading/
  // noPublicationsMessage had no existing Marathi anywhere — AI-authored, needs linguistic QA. The 7
  // PublicationType labels below reuse this file's own established terminology (e.g. "पात्र उमेदवार"
  // matching modules/i18n/pages/homeSummary.js's own "उमेदवार" usage) — also AI-authored, needs
  // linguistic QA, since these labels never existed anywhere in the app before (the homepage never
  // consumed this API until this phase).
  publications: {
    publicationsHeading: { en: 'Publications', mr: 'प्रकाशने' },
    noPublicationsMessage: { en: 'No publications available at this time.', mr: 'सध्या कोणतीही प्रकाशने उपलब्ध नाहीत.' },
    recruitmentAdvertisement: { en: 'Recruitment Advertisement', mr: 'भरती जाहिरात' },
    candidateUserGuide: { en: 'Candidate User Guide', mr: 'उमेदवार वापरकर्ता मार्गदर्शक' },
    importantInstructions: { en: 'Important Instructions', mr: 'महत्त्वाच्या सूचना' },
    otherNotice: { en: 'Notice', mr: 'सूचना' },
    eligibleCandidatesExamination: { en: 'Eligible Candidates — Examination', mr: 'पात्र उमेदवार — परीक्षा' },
    eligibleCandidatesInterview: { en: 'Eligible Candidates — Interview', mr: 'पात्र उमेदवार — मुलाखत' },
    selectedAndWaitingList: { en: 'Selected & Waiting Candidates', mr: 'निवड व प्रतीक्षा यादी उमेदवार' }
  },

  // LandingPage_new_one.jsx (Phase C.6.2 — Candidate Homepage Visual Modernization): new section
  // headings/labels for the modernized "Important Dates"/"Need Help?" sections and the shared
  // document-row "View" action. None existed anywhere in the app before this phase — all AI-authored,
  // needs linguistic QA. Kept as a small, dedicated namespace (rather than folded into `landingPage`)
  // since these are new C.6.2-specific labels, not extractions from pre-existing bilingual JSX.
  homepage: {
    viewAction: { en: 'View', mr: 'पहा' },
    importantDatesHeading: { en: 'Important Dates', mr: 'महत्त्वाच्या तारखा' },
    needHelpHeading: { en: 'Need Help?', mr: 'मदत हवी आहे?' },
    // Phase C.6.2.1: the 5 footer links (Cancellation/Refund, Contact Us, About Us, Privacy Policy,
    // Terms & Conditions) move into their own homepage card, per the Product Owner's explicit
    // clarification that they must not live inside the Need Help card. Same Drawer/content mechanism
    // (PublicFooter.jsx) — only the trigger location on the homepage changes.
    importantLinksHeading: { en: 'Important Links', mr: 'महत्त्वाचे दुवे' },
    refundPoliciesLink: { en: 'Cancellation/Refund Policies', mr: 'रद्दीकरण/परतावा धोरणे' },
    contactUsLink: { en: 'Contact Us', mr: 'आमच्याशी संपर्क साधा' },
    aboutUsLink: { en: 'About Us', mr: 'आमच्याबद्दल' },
    privacyPolicyLink: { en: 'Privacy Policy', mr: 'गोपनीयता धोरण' },
    termsConditionsLink: { en: 'Terms & Conditions', mr: 'नियम व अटी' }
  },

  // RegistrationMessage.jsx (Phase C.5). Extracted verbatim from the legacy modules/i18n.js
  // dictionary's thankYouRegistration/applicationId/loginInstruction/backToLogin entries — English and
  // Marathi wording unchanged. That legacy dictionary also carried a `hi` value for each of these keys,
  // but no centralized namespace anywhere in this file uses `hi` (EnabledLanguages today is en,mr only —
  // see this file's own header comment) and the legacy module never actually routed a live locale through
  // `hi` (every t() call site passed a literal 'en' or 'mr'), so nothing is lost by not carrying it
  // forward; a future Hindi expansion can add `hi` here the same way any other page would.
  registrationMessage: {
    thankYouRegistration: { en: 'Thank you! You have successfully completed this registration.', mr: 'धन्यवाद! तुम्ही ही नोंदणी यशस्वीरित्या पूर्ण केली आहे.' },
    applicationIdLabel: { en: 'Application ID:', mr: 'अर्ज आयडी' },
    loginInstruction: { en: 'Login using Application ID and Password to proceed further.', mr: 'पुढे जाण्यासाठी अर्ज आयडी आणि पासवर्ड वापरून लॉग इन करा.' },
    backToLoginButton: { en: 'Back To Login Page', mr: 'लॉगिन पृष्ठावर परत जा' }
  },

  // NavigationTab_new_one.jsx (Phase C.5). name/formNumber/appliedFor/dob/mobileNumber extracted
  // verbatim from the legacy modules/i18n.js dictionary (same hi-not-carried-forward rationale as
  // registrationMessage above). The remaining 4 keys migrate the file's "Update DOB" modal (JSX lines
  // 141-162 pre-migration) — that modal is dead/unreachable code today (its only trigger button is
  // commented out), migrated anyway for search-proof cleanliness at zero regression risk. Of those,
  // dateOfBirthLabel/selectBirthDatePlaceholder reuse real pre-existing Marathi text found adjacent to
  // them in the modal's own JSX (which already matches registration.dateOfBirthLabel's approved
  // wording); updateDobModalTitle/enterValidBirthDateError had no Marathi anywhere (the title mixed
  // English+Hindi, inconsistent with this file's own Marathi pattern) so `mr` is intentionally omitted
  // rather than inventing new translation content — they render English-only until a real translation
  // is authored, which is the correct, deterministic fallback behavior for a missing locale.
  navigationTab: {
    nameLabel: { en: 'Name:', mr: 'नाव' },
    formNumberLabel: { en: 'Form Number:', mr: 'फॉर्म क्रमांक' },
    appliedForLabel: { en: 'Applied For:', mr: 'पदा साठी अर्ज केला' },
    dobLabel: { en: 'DOB:', mr: 'जन्मतारीख' },
    mobileNumberLabel: { en: 'Mobile No.:', mr: 'मोबाईल क्रमांक' },
    updateDobModalTitle: { en: 'Update Date Of Birth' },
    dateOfBirthLabel: { en: 'Date of Birth', mr: 'जन्मतारीख' },
    selectBirthDatePlaceholder: { en: 'Select Birth Date', mr: 'जन्मतारीख निवडा' },
    enterValidBirthDateError: { en: 'Enter valid Birth Date' }
  },

  // components/enterprise/WizardSteps.jsx (Phase C.5.1 — Candidate Language Regression). Discovered
  // during C.5.1's authenticated-journey browser pass: this shared step-indicator (rendered from
  // NavigationTab_new_one.jsx whenever a wizard `currentStep` is active) hardcodes its 5 step titles
  // as plain English strings with no Marathi anywhere — a Sprint 2.4C addition that predates C.5 and
  // was never caught by C.5's own Devanagari-based search sweep (an English-only hardcoded string
  // doesn't show up in a Marathi-leak search). Fixed here as a small, isolated, immediately-verified
  // wiring correction per this phase's own §18 allowance — no business logic, API, or architecture
  // change; English wording preserved verbatim. mr is AI-authored, needs linguistic QA (no existing
  // Marathi anywhere in this file to extract from).
  wizardSteps: {
    personalInfo: { en: 'Personal Info', mr: 'वैयक्तिक माहिती' },
    qualification: { en: 'Qualification', mr: 'शैक्षणिक माहिती' },
    reservation: { en: 'Reservation', mr: 'आरक्षण' },
    photoSignature: { en: 'Photo & Signature', mr: 'फोटो आणि स्वाक्षरी' },
    paymentSubmit: { en: 'Payment & Submit', mr: 'पेमेंट आणि सबमिट' }
  },

  registration,
  personalInfo,
  qualificationInfo,
  reservationDetails,
  imageSignature,
  preview,
  challanDetails,
  submitForm,
  homeSummary,
  forgotPassword,
  examCard,
  receiptDetails,
  interviewCallLetter
};

export default DICTIONARY;
